import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { secureFetch } from './onboardingSlice';
import { baseOnboardingUrl } from './hooks';

const baseUrl = baseOnboardingUrl;

// AsyncThunk for updating generic onboarding status
export const updateGenericStatus = createAsyncThunk(
  'genericUpdate/updateGenericStatus',
  async ({ onboardingId, stage, newStatus, note, scheduledTime }) => {
    const data = {
      id: onboardingId,
      stage: stage,
      history: {
        timestamp: new Date().toISOString(),
        event: {
          from: null, // Will be updated based on current state
          to: newStatus
        },
        trigger: {
          source: "user",
          id: "user-id"
        },
        data: {},
        note: note
      }
    };

    // Add scheduledTime to data if status is "scheduled" and scheduledTime is set
    if (newStatus === "scheduled" && scheduledTime) {
      data.history.data.scheduledTime = scheduledTime.toISOString();
    }

    console.log("Updating generic status with data: ", data);

    const response = await secureFetch(`${baseUrl}/api/onboardings_view/general/update_generic`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update generic status');
    }

    const result = await response.json();
    console.log("Generic status update response: ", result);
    return result;
  }
);

const initialState = {
  // Form state
  newStatus: null,
  note: null,
  scheduledTime: null,
  showForm: false,
  
  // Loading and error states
  loading: false,
  error: null,
  
  // Success state
  success: false
};

const genericUpdateSlice = createSlice({
  name: 'genericUpdate',
  initialState,
  reducers: {
    setNewStatus: (state, action) => {
      state.newStatus = action.payload;
      // Reset scheduled time if status changes away from "scheduled"
      if (action.payload !== "scheduled") {
        state.scheduledTime = null;
      }
    },
    setNote: (state, action) => {
      state.note = action.payload;
    },
    setScheduledTime: (state, action) => {
      state.scheduledTime = action.payload;
    },
    toggleForm: (state) => {
      state.showForm = !state.showForm;
      // Reset form state when closing
      if (state.showForm) {
        state.newStatus = null;
        state.note = null;
        state.scheduledTime = null;
        state.error = null;
        state.success = false;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    resetForm: (state) => {
      state.newStatus = null;
      state.note = null;
      state.scheduledTime = null;
      state.error = null;
      state.success = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateGenericStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateGenericStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        // Reset form after successful update
        state.newStatus = null;
        state.note = null;
        state.scheduledTime = null;
        state.showForm = false;
      })
      .addCase(updateGenericStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.success = false;
      });
  }
});

export const {
  setNewStatus,
  setNote,
  setScheduledTime,
  toggleForm,
  clearError,
  clearSuccess,
  resetForm
} = genericUpdateSlice.actions;

// Selectors
export const selectNewStatus = (state) => state.genericUpdate.newStatus;
export const selectNote = (state) => state.genericUpdate.note;
export const selectScheduledTime = (state) => state.genericUpdate.scheduledTime;
export const selectShowForm = (state) => state.genericUpdate.showForm;
export const selectLoading = (state) => state.genericUpdate.loading;
export const selectError = (state) => state.genericUpdate.error;
export const selectSuccess = (state) => state.genericUpdate.success;

export default genericUpdateSlice.reducer;
